import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Button} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

import CheckboxCheckIcon from '../../components/icons/shared/others/CheckboxCheckIcon'
import {useGigyaModal} from '../../context/gigya-modal'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {getFormattedEntryDateString, setWithExpiry, getWithExpiry} from '../../utils/utils'
import {useLocation} from 'react-router-dom'
import useUtils from '../../custom-hooks/use-utils-helper'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event} from '../../custom-api/layers/default'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import LoadingSpinner from '../loading-spinner'
import {BRAND_SHORT_LABEL} from '../../brand.ts'
import {useTokenHelper} from '../../utils/token-helper.js'
import {remCalc} from '../../theme/_utils'

const NotifyMeButton = ({
    product,
    compactView = false,
    isPlp = false,
    size = 'large',
    height = remCalc(36),
    variant,
}) => {
    const {data: customer} = useCurrentCustomer()
    const {toggleNotifyProduct, isProductNotified, openModal} = useGigyaModal()
    const {data: basket} = useCurrentBasket()
    const appOrigin = useOrigin(false)
    const [isLoading, setIsLoading] = useState(false)
    const {fetchMCValidToken} = useTokenHelper()

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const [error, setError] = useState(null)
    const [isPressed, setIsPressed] = useState(false)
    const {locale} = useMultiSite()
    const {addLayer} = useGoogleTagManager()
    const location = useLocation()
    const {isWishlistPage, isProductDetail} = useUtils().currentPage(location.pathname)
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const ONE_DAY_MS = 24 * 60 * 60 * 1000
    const LOCAL_KEY_PREFIX = 'NotifyMePressed_'

    const productId = isProductDetail
        ? product?.id
        : isWishlistPage
        ? product?.productId
        : product?.representedProduct?.id

    useEffect(() => {
        const localStoragePressed = getWithExpiry(`${LOCAL_KEY_PREFIX}${productId}`)
        const isNotified = isProductNotified(productId) || localStoragePressed
        setIsPressed(isNotified)
    }, [productId, isProductNotified])

    // Trigger successful notify me flow email event to Marketing Cloud
    const triggerNotifyEmailEvent = async (dataWithIds) => {
        try {
            // Check if we have email
            if (!dataWithIds?.email) {
                console.error('Email is required for notification')
                return
            }

            setIsLoading(true)
            const accessToken = await fetchMCValidToken()

            const triggerEmailPayload = {
                contactKey: dataWithIds?.userId,
                data: {
                    id_gigya: dataWithIds?.userId,
                    email: dataWithIds?.email, // Use email from dataWithIds instead of customer
                    sku: productId,
                    EntryDate: getFormattedEntryDateString(new Date()),
                    locale: locale.id,
                    brand: BRAND_SHORT_LABEL,
                },
            }

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/trigger-notify-flow-email-event-marketing-cloud`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(triggerEmailPayload),
                },
            )

            const result = await response.json()

            if (result.success === true) {
                setIsPressed(true)
                setWithExpiry(`${LOCAL_KEY_PREFIX}${productId}`, productId, ONE_DAY_MS)
                toggleNotifyProduct(productId)
            } else {
                setError('Email event triggering failed: ' + result.error)
                console.error('Email event triggering failed: ' + result.error)
            }
        } catch (error) {
            console.error('Error during  notify flow:', error)
            setError('Error during notify flow: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    // Modify handleNotification to be used as callback after login
    const handleNotification = async (data) => {
        const loggedInUser = JSON.parse(localStorage.getItem('DL-NotifyMe-Customer'))
        try {
            const dataWithIds = {
                ...data,
                userId: customer?.c_gigyaId || basket?.c_gigyaGuestId || loggedInUser?.c_gigyaId,
                email: customer?.email || loggedInUser?.email, // Add email to dataWithIds
                product: product,
            }
            setIsLoading(true)
            await addLayer(
                item_gtm_event.bind(this, {
                    event: 'stock-alert',
                    items: [
                        {
                            item_name: product?.c_defaultName,
                            item_id: productId,
                            item_category: product?.c_defaultCategoryName,
                        },
                    ],
                }),
            )
            await triggerNotifyEmailEvent(dataWithIds)
        } catch (error) {
            console.error('Error:', error)
        }
    }

    // Add new click handler
    const handleClick = () => {
        if (customer?.isGuest) {
            openModal(true, handleNotification)
        } else {
            handleNotification()
        }
    }

    // Update the Button component
    return (
        <Box className="notify-me-button" w={isPlp ? 'max-content' : 'full'}>
            {isLoading && <LoadingSpinner />}

            {!notShoppableCountry && (
                <Button
                    w={isPlp ? 'max-content' : 'full'}
                    h={height}
                    p={isPlp ? {base: 'xxxsmall-1.base', lg: 'xsmall.base'} : undefined}
                    onClick={handleClick}
                    iconSpacing="medium-1"
                    borderRadius={isPlp ? 'large' : undefined}
                    whiteSpace="nowrap"
                    isDisabled={!customer?.isGuest && isPressed}
                    variant={
                        variant ||
                        (!isPlp
                            ? !customer?.isGuest && isPressed
                                ? 'primary'
                                : 'outline'
                            : 'outline')
                    }
                    size={size}
                    sx={
                        !customer?.isGuest && isPressed
                            ? {
                                  bg: 'background.positive.default',
                                  color: 'foreground.neutral.inverse',
                                  cursor: 'not-allowed',
                                  _hover: {
                                      bg: 'background.positive.default',
                                  },
                                  _disabled: {
                                      opacity: 1,
                                      cursor: 'not-allowed',
                                      boxShadow: 'none',
                                      bg: 'background.positive.default',
                                      color: 'foreground.neutral.inverse',
                                      _hover: {
                                          bg: 'background.positive.default',
                                      },
                                      _active: {
                                          bg: 'background.positive.default',
                                      },
                                  },
                              }
                            : {}
                    }
                    leftIcon={
                        !compactView && !customer?.isGuest && isPressed ? (
                            <CheckboxCheckIcon
                                boxSize={remCalc(16)}
                                color="foreground.neutral.inverse"
                            />
                        ) : undefined
                    }
                >
                    {!customer?.isGuest && isPressed ? (
                        <FormattedMessage
                            id="global.notify-me-button-unsubscribed"
                            defaultMessage="Notified"
                        />
                    ) : (
                        <FormattedMessage id="global.notify-me-button" defaultMessage="Notify Me" />
                    )}
                </Button>
            )}
        </Box>
    )
}

NotifyMeButton.propTypes = {
    product: PropTypes.oneOfType([
        PropTypes.shape({
            type: PropTypes.object,
            productType: PropTypes.object,
            representedProduct: PropTypes.object,
            variationAttributes: PropTypes.array,
        }),
        PropTypes.arrayOf(
            PropTypes.shape({
                type: PropTypes.object,
                productType: PropTypes.object,
                representedProduct: PropTypes.object,
                variationAttributes: PropTypes.array,
            }),
        ),
    ]).isRequired,
    compactView: PropTypes.bool,
    isPlp: PropTypes.bool,
    isBackorder: PropTypes.bool,
    size: PropTypes.string,
    height: PropTypes.string,
    variant: PropTypes.string,
}

NotifyMeButton.displayName = 'NotifyMeButton'

export default NotifyMeButton
